import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Banknote, Play, Check, X } from "lucide-react";
import { Stat, Section, Loading, Empty, Pill, useFetch, money, dt } from "./_shared";

export default function Payouts() {
  const { data: spark, loading } = useFetch("/admin/affiliate/growth-sparkline?days=90");
  const { data: lbData, reload: reloadLb } = useFetch("/admin/affiliate/leaderboard?days=30&sort=total");
  const { data: histData, reload: reloadHist } = useFetch("/admin/affiliate/payout-history");
  const [busy, setBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState(null);
  const [result, setResult] = useState(null);
  const [pct, setPct] = useState({});
  if (loading) return <Loading />;

  const lb = lbData?.leaderboard || []; const totals = lbData?.totals || {};
  const payouts = histData?.payouts || [];

  const runPayout = async () => {
    if (!window.confirm("Monatliche Affiliate-Auszahlung jetzt ausführen? Alle freigegebenen (cleared) Rewards werden als Credits gutgeschrieben.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/affiliate/run-monthly-payout");
      setResult(data);
      toast.success(`${data.processed ?? 0} Rewards ausgezahlt · ${data.credits_minted ?? 0} Credits`);
      await reloadHist();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const approve = async (uid) => {
    const commission = Math.max(0, Math.min(100, Number(pct[uid] ?? 20)));
    setRowBusy(uid);
    try { await api.post(`/admin/affiliate/${uid}/approve`, { commission_percent: commission }); toast.success("Affiliate freigeschaltet."); await reloadLb(); }
    catch (err) { toast.error(apiError(err)); } finally { setRowBusy(null); }
  };
  const reject = async (uid) => {
    setRowBusy(uid);
    try { await api.post(`/admin/affiliate/${uid}/reject`, { reason: "Nicht passend" }); toast.success("Abgelehnt."); await reloadLb(); }
    catch (err) { toast.error(apiError(err)); } finally { setRowBusy(null); }
  };

  const statusTone = { active: "good", pending: "warn", rejected: "bad", none: "default" };

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Admin" title="Auszahlungen" subtitle="Affiliate-Provisionen, monatlicher Payout & Freischaltungen."
        action={<Button size="sm" className="gap-1.5 rounded-full" disabled={busy} onClick={runPayout} data-testid="admin-payout-run"><Play className="h-4 w-4" /> Monatliche Auszahlung ausführen</Button>} />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Volumen 90T" value={money(spark?.total_cents)} testId="admin-payout-volume" />
        <Stat label="Spitzentag" value={money(spark?.peak_cents)} />
        <Stat label="Trend" value={`${spark?.trend_pct ?? 0}%`} tone={(spark?.trend_pct ?? 0) >= 0 ? "good" : "bad"} />
        <Stat label="Referrer (30T)" value={totals.referrers ?? 0} hint={`${totals.conversions ?? 0} Conversions`} />
      </div>

      {result && (
        <Section title="Letzte Ausführung" testId="admin-payout-result">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Verarbeitet" value={result.processed ?? 0} tone="good" />
            <Stat label="Credits gutgeschrieben" value={result.credits_minted ?? 0} />
            <Stat label="Aktive Affiliates" value={result.affiliates ?? 0} />
            <Stat label="Übersprungen" value={result.skipped ?? 0} tone={result.skipped ? "warn" : "default"} />
          </div>
        </Section>
      )}

      <Section title="Affiliate-Leaderboard (30T) — freischalten / ablehnen" testId="admin-payout-leaderboard">
        {lb.length === 0 ? <Empty text="Noch keine Conversions." /> : (
          <div className="space-y-2">
            {lb.map((r, i) => (
              <div key={r.referrer_id} data-testid={`admin-payout-aff-${r.referrer_id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-background p-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{i + 1}. {r.name} <Pill tone={statusTone[r.affiliate_status] || "default"}>{r.affiliate_status}</Pill></p>
                  <p className="truncate text-xs text-muted-foreground">{r.email} · {r.conversions} Conv. · offen {money(r.pending_cents)} · ausgezahlt {money(r.paid_cents)}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-semibold text-foreground">{money(r.total_cents)}</span>
                  {r.affiliate_status !== "active" && (
                    <>
                      <Input type="number" value={pct[r.referrer_id] ?? "20"} onChange={(e) => setPct((p) => ({ ...p, [r.referrer_id]: e.target.value }))} className="h-8 w-16" title="Provision %" data-testid={`admin-payout-pct-${r.referrer_id}`} />
                      <Button size="sm" variant="outline" className="rounded-full text-emerald-600" disabled={rowBusy === r.referrer_id} onClick={() => approve(r.referrer_id)} data-testid={`admin-payout-approve-${r.referrer_id}`}><Check className="h-4 w-4" /></Button>
                    </>
                  )}
                  {r.affiliate_status !== "rejected" && (
                    <Button size="sm" variant="outline" className="rounded-full text-rose-600" disabled={rowBusy === r.referrer_id} onClick={() => reject(r.referrer_id)} data-testid={`admin-payout-reject-${r.referrer_id}`}><X className="h-4 w-4" /></Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Auszahlungs-Historie" testId="admin-payout-history">
        {payouts.length === 0 ? <Empty text="Noch keine automatischen Auszahlungen." /> : (
          <div className="space-y-1.5">
            {payouts.map((p, i) => {
              const s = p.stats || {};
              return (
                <div key={p.id || i} className="flex items-center justify-between gap-2 rounded-xl border border-border bg-background p-3 text-sm">
                  <span className="flex items-center gap-2 text-muted-foreground"><Banknote className="h-4 w-4" /> {dt(p.created_at)}</span>
                  <span className="shrink-0 text-xs text-foreground">{s.processed ?? 0} Rewards · {s.credits_minted ?? 0} Credits · {s.affiliates ?? 0} Affiliates</span>
                </div>
              );
            })}
          </div>
        )}
      </Section>
    </div>
  );
}
